import { useEffect, useState } from 'react';

import { Avatar, Box, HStack, Text, Tooltip } from '@bonusly/design-system';

import { ActionCableProvider } from '../providers/action-cable-provider';

interface ConnectedUsersProps {
	provider: ActionCableProvider;
	maxVisible?: number;
	showLabel?: boolean;
}

interface AwarenessUser {
	id: string;
	name: string;
	avatar?: string;
	color: string;
}

export function ConnectedUsers({ provider, maxVisible = 5, showLabel = false }: ConnectedUsersProps) {
	const [users, setUsers] = useState<AwarenessUser[]>([]);

	useEffect(() => {
		const updateUsers = () => {
			const states = provider.awareness.getStates();
			const currentUserState = states.get(provider.doc.clientID);
			const currentUserId = currentUserState?.user?.id;

			const userMap = new Map<string, AwarenessUser>();

			states.forEach((state: any, clientId: number) => {
				// Skip if no user data
				if (!state.user) {
					return;
				}

				const userId = state.user.id || String(clientId);

				// Skip current user (by user ID, not just clientId)
				if (userId === currentUserId) {
					return;
				}

				if (!userMap.has(userId)) {
					userMap.set(userId, {
						id: userId,
						name: state.user.name || 'Anonymous',
						avatar: state.user.avatar,
						color: state.user.color || '#999',
					});
				}
			});

			setUsers(Array.from(userMap.values()));
		};

		updateUsers();

		// Listen for awareness changes (users joining, leaving, or updating info)
		provider.awareness.on('change', updateUsers);

		return () => {
			provider.awareness.off('change', updateUsers);
		};
	}, [provider]);

	if (users.length === 0) {
		return null;
	}

	const visibleUsers = users.slice(0, maxVisible);
	const hiddenCount = users.length - maxVisible;

	return (
		<HStack spacing={2} align="center">
			{showLabel && (
				<Text fontSize="sm" color="text.softer">
					{users.length === 1 ? '1 user online' : `${users.length} users online`}
				</Text>
			)}

			<HStack spacing={-2}>
				{visibleUsers.map((user) => (
					<Tooltip key={user.id} label={user.name} placement="bottom">
						<Box
							borderRadius="full"
							borderWidth="2px"
							borderColor={user.color}
							p="2px"
							bg="bg.surface"
							transition="transform 0.2s"
							_hover={{
								transform: 'scale(1.1)',
								zIndex: 10,
							}}
						>
							{user.avatar ? <Avatar src={user.avatar} name={user.name} size="sm" /> : <Avatar name={user.name} size="sm" bg={user.color} />}
						</Box>
					</Tooltip>
				))}

				{hiddenCount > 0 && (
					<Tooltip label={`+${hiddenCount} more`} placement="bottom">
						<Box
							borderRadius="full"
							borderWidth="2px"
							borderColor="gray.300"
							p="2px"
							bg="bg.surface"
							display="flex"
							alignItems="center"
							justifyContent="center"
							w="40px"
							h="40px"
						>
							<Text fontSize="xs" fontWeight="semibold" color="text.softer">
								+{hiddenCount}
							</Text>
						</Box>
					</Tooltip>
				)}
			</HStack>
		</HStack>
	);
}
